import type { StoreDefinition } from 'pinia'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { RouteLocationNormalized } from 'vue-router'

import type { BulkAction } from '@/javascript/components/bulk_actions_dialog'
import type {
	Agency,
	AgencyBackOffice,
	AgencyInformation,
	CAMember,
	CurrentUser,
	ErrorType,
	Language,
} from '@/javascript/interfaces'
import { standardDateFormat } from '@/javascript/lib/validate_date/process-date-string'
import type { CABreadcrumbsLink, CACommandResult } from '@ds/interfaces'
import { COUNTDOWN, DELAY } from '@vuejs/helpers/_constants'

import type { MemberAction } from '../services/administratorDashboard/interfaces'
import api from '../services/api'
import type { Member } from '../services/members/interfaces'
import { getAgencyTeam } from '../services/team'
import type { State } from '../services/trainingCurriculums/interfaces'
import { getTrialDetails } from '../services/trialSeatUpgrade'
import { getAgencyId } from '../utils/getAgencyId'
import { getFranchisorId } from '../utils/getFranchisorId'
import { getIdFromUrl } from '../utils/getIdFromUrl'
import { useAdministratorStore } from './AdministratorStore'
import { useCurriculumsStore } from './CurriculumsStore'
import { useFeatureFlagStore } from './FeatureFlagStore'
import { useFranchisorStore } from './FranchisorStore'
import { useMemberTrainingStore } from './MemberTrainingStore'
import { useNotificationStore } from './NotificationStore'
import { useTeamStore } from './TeamStore'

export type PopoverOptions = '' | 'Edit name' | 'Manage due date'

const STATES_LIST: State[] = [
	{
		abbreviation: 'AB',
		name: 'Alberta',
		timezone: 'Mountain Time (US & Canada)',
	},
	{
		abbreviation: 'AK',
		name: 'Alaska',
		timezone: 'Alaska',
	},
	{
		abbreviation: 'AL',
		name: 'Alabama',
		timezone: 'Central Time (US & Canada)',
	},
	{
		abbreviation: 'AR',
		name: 'Arkansas',
		timezone: 'Mountain Time (US & Canada)',
	},
	{
		abbreviation: 'AZ',
		name: 'Arizona',
		timezone: 'Mountain Time (US & Canada)',
	},
	{
		abbreviation: 'BC',
		name: 'British Columbia',
		timezone: 'Pacific Time (US & Canada)',
	},
	{
		abbreviation: 'CA',
		name: 'California',
		timezone: 'Pacific Time (US & Canada)',
	},
	{
		abbreviation: 'CO',
		name: 'Colorado',
		timezone: 'Mountain Time (US & Canada)',
	},
	{
		abbreviation: 'CT',
		name: 'Connecticut',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'DC',
		name: 'District of Columbia',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'DE',
		name: 'Delaware',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'FL',
		name: 'Florida',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'GA',
		name: 'Georgia',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'HI',
		name: 'Hawaii',
		timezone: 'Hawaii',
	},
	{
		abbreviation: 'IA',
		name: 'Iowa',
		timezone: 'Central Time (US & Canada)',
	},
	{
		abbreviation: 'ID',
		name: 'Idaho',
		timezone: 'Mountain Time (US & Canada)',
	},
	{
		abbreviation: 'IL',
		name: 'Illinois',
		timezone: 'Central Time (US & Canada)',
	},
	{
		abbreviation: 'IN',
		name: 'Indiana',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'KS',
		name: 'Kansas',
		timezone: 'Central Time (US & Canada)',
	},
	{
		abbreviation: 'KY',
		name: 'Kentucky',
		timezone: 'Central Time (US & Canada)',
	},
	{
		abbreviation: 'LA',
		name: 'Louisiana',
		timezone: 'Central Time (US & Canada)',
	},
	{
		abbreviation: 'MA',
		name: 'Massachusetts',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'MB',
		name: 'Manitoba',
		timezone: 'Central Time (US & Canada)',
	},
	{
		abbreviation: 'MD',
		name: 'Maryland',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'ME',
		name: 'Maine',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'MI',
		name: 'Michigan',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'MN',
		name: 'Minnesota',
		timezone: 'Central Time (US & Canada)',
	},
	{
		abbreviation: 'MO',
		name: 'Missouri',
		timezone: 'Central Time (US & Canada)',
	},
	{
		abbreviation: 'MS',
		name: 'Mississippi',
		timezone: 'Central Time (US & Canada)',
	},
	{
		abbreviation: 'MT',
		name: 'Montana',
		timezone: 'Mountain Time (US & Canada)',
	},
	{
		abbreviation: 'NB',
		name: 'New Brunswick',
		timezone: 'Atlantic Time (Canada)',
	},
	{
		abbreviation: 'NC',
		name: 'North Carolina',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'ND',
		name: 'North Dakota',
		timezone: 'Central Time (US & Canada)',
	},
	{
		abbreviation: 'NE',
		name: 'Nebraska',
		timezone: 'Central Time (US & Canada)',
	},
	{
		abbreviation: 'NH',
		name: 'New Hampshire',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'NJ',
		name: 'New Jersey',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'NL',
		name: 'Newfoundland and Labrador',
		timezone: 'Newfoundland',
	},
	{
		abbreviation: 'NM',
		name: 'New Mexico',
		timezone: 'Mountain Time (US & Canada)',
	},
	{
		abbreviation: 'NS',
		name: 'Nova Scotia',
		timezone: 'Atlantic Time (Canada)',
	},
	{
		abbreviation: 'NU',
		name: 'Nunavut',
		timezone: 'Central Time (US & Canada)',
	},
	{
		abbreviation: 'NV',
		name: 'Nevada',
		timezone: 'Pacific Time (US & Canada)',
	},
	{
		abbreviation: 'NY',
		name: 'New York',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'OH',
		name: 'Ohio',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'OK',
		name: 'Oklahoma',
		timezone: 'Central Time (US & Canada)',
	},
	{
		abbreviation: 'ON',
		name: 'Ontario',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'OR',
		name: 'Oregon',
		timezone: 'Pacific Time (US & Canada)',
	},
	{
		abbreviation: 'PA',
		name: 'Pennsylvania',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'PE',
		name: 'Prince Edward Island',
		timezone: 'Atlantic Time (Canada)',
	},
	{
		abbreviation: 'PR',
		name: 'Puerto Rico',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'QC',
		name: 'Quebec',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'RI',
		name: 'Rhode Island',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'SC',
		name: 'South Carolina',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'SD',
		name: 'South Dakota',
		timezone: 'Central Time (US & Canada)',
	},
	{
		abbreviation: 'SK',
		name: 'Saskatchewan',
		timezone: 'Central Time (US & Canada)',
	},
	{
		abbreviation: 'TN',
		name: 'Tennessee',
		timezone: 'Central Time (US & Canada)',
	},
	{
		abbreviation: 'TX',
		name: 'Texas',
		timezone: 'Central Time (US & Canada)',
	},
	{
		abbreviation: 'VI',
		name: 'U.S. Virgin Islands',
		timezone: 'Atlantic Time (Canada)',
	},
	{
		abbreviation: 'UT',
		name: 'Utah',
		timezone: 'Mountain Time (US & Canada)',
	},
	{
		abbreviation: 'VA',
		name: 'Virginia',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'VT',
		name: 'Vermont',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'WA',
		name: 'Washington',
		timezone: 'Pacific Time (US & Canada)',
	},
	{
		abbreviation: 'WI',
		name: 'Wisconsin',
		timezone: 'Central Time (US & Canada)',
	},
	{
		abbreviation: 'WV',
		name: 'West Virginia',
		timezone: 'Eastern Time (US & Canada)',
	},
	{
		abbreviation: 'WY',
		name: 'Wyoming',
		timezone: 'Mountain Time (US & Canada)',
	},
]

export const useGlobalStore: StoreDefinition<string, any, any, any> = defineStore('global', () => {
	// State
	const administratorStore = useAdministratorStore()
	const agencies = ref<CACommandResult[]>([])
	const agency = ref<Agency | null | undefined>(null)
	const agencyBackOffice = ref<AgencyBackOffice>({
		backOfficeIntegrationEnabled: false,
		backOfficeIntegrationProviderName: '',
		marketingProductName: '',
	})
	const agencyCreatedAt = ref('')
	const agencyInfo = ref<AgencyInformation>({} as AgencyInformation)
	const agencyMembers = ref<Member[]>([])
	const bulkAction = ref<BulkAction>({
		action: '' as MemberAction,
		actionDescriptionText: '',
		actionText: '',
		additionalMembersText: <></>,
		bodyText: '',
		memberIds: [] as number[],
		membersText: '',
	})
	const currentUser = ref<CurrentUser | null | undefined>(null)
	const curriculumsStore = useCurriculumsStore()
	const emittedEvent = ref('')
	const franchisorSSOPath = ref('')
	const isAdvanceReporting = ref(false)
	const isSharedCompletionsEnabled = ref(false)
	const loadingAgencyInfo = ref(false)
	const memberTrainingStore = useMemberTrainingStore()
	const notificationStore = useNotificationStore()
	const page = ref('')
	const previousRoute = ref<RouteLocationNormalized | null>(null)
	const previousRouteUrl = ref('')
	const selectAllChecked = ref(false)
	const selectAllCheckedOnPage = ref<{ [page: number]: boolean }>({})
	const selectedCATableMembers = ref<CAMember[]>([])
	const selectedLanguage = ref<Language>('' as Language)
	const showPopover = ref<{ id: number | null; name: PopoverOptions }>({
		id: null,
		name: '',
	})
	const showTrainingDashboard = ref(true)
	const simpleHeaderLayout = ref(false)
	const twilioSuccessCode = ref('')
	const twilioUserErrorMessages = ref<{ [key: string]: string }>({})
	const userId = ref('')

	// Getters
	const agencyId = computed((): number => getAgencyId())

	const someSelectAllCheckboxIsTrue = computed((): boolean =>
		Object.values(selectAllCheckedOnPage.value).some(value => value === true)
	)

	const selectedCheckboxNotOnPage = computed(
		(): boolean => !selectAllCheckedOnPage.value[useTeamStore().pagination.currentPage]
	)

	const checkedOnAnotherPage = computed(
		(): boolean => someSelectAllCheckboxIsTrue.value && selectedCheckboxNotOnPage.value
	)

	const currentDate = computed((): string => {
		const dateFormatOptions: Intl.DateTimeFormatOptions = {
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			month: 'long',
			second: '2-digit',
			timeZoneName: 'longOffset',
			weekday: 'short',
			year: 'numeric',
		}

		return new Date().toLocaleString('en-US', dateFormatOptions)
	})

	const currentDateString = computed((): string => standardDateFormat(new Date()))

	const currentUrl = computed((): string => window.location.href)

	const eligibleAgenciesUrl = computed((): string => '/api/agencies/eligible_agencies')

	const franchisorId = computed((): number | null => currentUser.value?.franchisor?.id || getFranchisorId())

	const hasFranchisorId = computed((): boolean => !!getFranchisorId())

	const franchisorLink = computed((): CABreadcrumbsLink[] => {
		const computedFranchisorId = currentUser.value?.franchisor?.id || getFranchisorId()

		return [
			{
				active: pageIsFranchisor.value,
				dataTest: 'navigation-breadcrumbs-franchisor-link',
				name: computedFranchisorId ? 'Multi-location Dashboard' : 'Dashboard',
				to: computedFranchisorId ? `/agencies?franchisor_id=${computedFranchisorId}` : '/',
			},
		]
	})

	const hasAgency = computed((): boolean => !!(agency.value?.id || currentUser.value?.agency?.id))

	const inFreeTrial = computed(
		(): boolean =>
			agency.value?.status === 'free_trial' ||
			currentUser.value?.agency?.status === 'free_trial' ||
			agencyInfo.value?.is_in_free_trial ||
			false
	)

	const isAdvanceReportingEnabled = computed((): boolean => isAdvanceReporting.value)

	const isBillingAdmin = computed(
		(): boolean =>
			currentUser.value?.isSuper ||
			currentUser.value?.isFranchisorManager ||
			currentUser.value?.isAgencyBillingManager ||
			currentUser.value?.isAgencyOwner ||
			false
	)

	const isCurriculumDiscoverabilityEnabled = computed((): boolean =>
		useFeatureFlagStore().isFeatureEnabled('curriculum_discoverability')
	)

	const isFranchisor = computed((): boolean => currentUser.value?.isFranchisorManager || false)

	const isFromTeamPage = computed(
		(): boolean => previousRouteUrl.value.includes('team') || currentUrl.value.includes('team')
	)

	const isMemberOnly = computed(
		(): boolean =>
			!currentUser.value?.isSuper &&
			!currentUser.value?.isAgencyBillingManager &&
			!currentUser.value?.isAgencyManager &&
			!currentUser.value?.isFranchisorManager &&
			!currentUser.value?.isMoreThanCaregiver &&
			(currentUser.value?.isCaregiver || false)
	)

	const isMobileView = computed((): boolean => {
		const element = document.querySelector('body')

		return element ? element.classList.contains('mobile-view') : false
	})

	const isMultiLocation = computed((): boolean => page.value === 'franchisor')

	const pageIsDashboard = computed((): boolean => currentUrl.value.includes('compliance_report'))

	const pageIsFranchisor = computed((): boolean => {
		const franchisorStore = useFranchisorStore()

		return (
			currentUrl.value === `/agencies?franchisor_id=${franchisorStore.franchisorId}` ||
			hasFranchisorId.value &&
				agencies.value.length >= 1 &&
				currentUrl.value === '/agencies' ||
			hasFranchisorId.value &&
				agencies.value.length >= 1 &&
				currentUrl.value === `/agencies?franchisor_id=${franchisorStore.franchisorId}`
		)
	})

	const showAddAllToOrganizations = computed((): boolean => {
		if (!currentUser.value) {
			return false
		}

		return (
			(currentUser.value.isFranchisorManager || currentUser.value.isSuper) &&
			!!currentUser.value.franchisor
		)
	})

	const showFranchise = computed((): boolean => {
		if (!currentUser.value) {
			return false
		}

		return currentUser.value.isFranchisorManager || currentUser.value.isSuper
	})

	const states = computed((): State[] => STATES_LIST)

	const teamBreadcrumbLink = computed((): CABreadcrumbsLink[] => {
		const goBackToPage = window.sessionStorage.getItem('goBackToPage')

		const showInsightsLink =
			!goBackToPage?.includes('team') &&
			!!window.sessionStorage.getItem('selectedGroupId') &&
			page.value !== 'team' &&
			!previousRouteUrl.value.includes('team')

		const hideTeamAndInsightsLinks = page.value === 'catalog'

		switch (true) {
			case hideTeamAndInsightsLinks:
				return []

			case page.value === 'designSystem':
				return [
					{
						active: false,
						dataTest: 'navigation-breadcrumbs-design-system',
						name: 'Design System',
						to: '/design-system',
					},
				]

			case showInsightsLink:
				return [
					{
						active: false,
						dataTest: 'navigation-breadcrumbs-insights',
						name: 'Insights',
						to: `/agencies/${getAgencyId()}/compliance_report`,
					},
				]

			default:
				return [
					{
						active: false,
						dataTest: 'navigation-breadcrumbs-team',
						name: 'Team',
						to: `/agencies/${getAgencyId()}/team`,
					},
				]
		}
	})

	// Actions
	const checkAndDisplaySessionStorageMessage = (): void => {
		const successMessage = window.sessionStorage.getItem('successMessage')
		const sharedCompletionsFound = window.sessionStorage.getItem('sharedCompletionsFound')
		const errorMessage = window.sessionStorage.getItem('errorMessage')

		if (successMessage) {
			notificationStore.onHandleSuccessMessages({ message: successMessage })
			window.sessionStorage.removeItem('successMessage')
		}

		if (errorMessage) {
			notificationStore.onHandleErrorMessages({ error: { message: errorMessage } })
			window.sessionStorage.removeItem('errorMessage')
		}

		if (sharedCompletionsFound) {
			notificationStore.onHandleSuccessMessages({ message: sharedCompletionsFound, type: 'info' })
			window.sessionStorage.removeItem('sharedCompletionsFound')
		}
	}

	const clearSelectedValues = (): void => {
		selectedCATableMembers.value = []
		selectAllCheckedOnPage.value = {}
		selectAllChecked.value = false
	}

	const closeModal = (): void => {
		emittedEvent.value = ''
		selectedCATableMembers.value = []

		administratorStore.selectedMembers = []
		memberTrainingStore.resetData()
		curriculumsStore.resetData()
	}

	const closePopover = (): void => {
		showPopover.value = { id: null, name: '' }
	}

	const countDownTimer = ({
		newMessage,
		response,
	}: {
		newMessage?: string
		response?: { data?: { success_messages?: string } }
	}): void => {
		let countdown = COUNTDOWN
		const interval = setInterval(() => {
			countdown--
			if (newMessage) {
				notificationStore.onHandleSuccessMessages({ message: `${newMessage} Refreshing page in ${countdown} seconds.` })
			}

			if (response && response.data?.success_messages) {
				notificationStore
					.onHandleSuccessMessages({ message: `${response.data.success_messages} Refreshing page in ${countdown} seconds.` })
			}

			if (countdown === 0) {
				clearInterval(interval)
				window.location.reload()
			}
		}, DELAY.ONE_SECOND)
	}

	const fillAgencyBackOffice = async (): Promise<void> => {
		try {
			const response = await getAgencyTeam(api, getAgencyId())

			agencyBackOffice.value = {
				backOfficeIntegrationEnabled: response.agency?.back_office_integration_enabled,
				backOfficeIntegrationProviderName: response.agency?.back_office_integration_provider_name,
				marketingProductName: response.agency?.marketing_product_name,
			}
		} catch (error) {
			notificationStore.onHandleErrorMessages({ error: error as ErrorType })
		}
	}

	const formatSeatsUsed = (seatUsed = 0): number => {
		return Math.trunc(seatUsed) ?? 0
	}

	const getAgencyInfo = async (): Promise<void> => {
		loadingAgencyInfo.value = true
		try {
			const id = getIdFromUrl()

			if (id !== 0) {
				const response = await getTrialDetails(api, `${id}`)
				const { data } = response

				if (data.agency) {
					agencyInfo.value = {
						...agencyInfo.value,
						days_left: data.agency.days_left,
						is_expired: data.agency.is_expired,
						total_trial_days: data.agency.total_trial_days,
					}
				}
			}
		} catch (error) {
			notificationStore.onHandleErrorMessages({ error: error as ErrorType })
		} finally {
			loadingAgencyInfo.value = false
		}
	}

	const getTwilioUserErrorMessage = (errorCode: string): string => {
		return twilioUserErrorMessages.value[errorCode]
	}

	const hasTwilioUserError = (errorCode: string): boolean => {
		return Object.prototype.hasOwnProperty.call(twilioUserErrorMessages.value, errorCode)
	}

	const identifyPage = (url: string): string => {
		const teamPagePattern = /^\/agencies\/\d+\/team\/?$/

		const groupMembersPagePattern = /^\/agencies\/\d+\/team\/\d+\/members\/?(\d+\/?)?$/

		if (teamPagePattern.test(url)) {
			return 'teamPage'
		} else if (groupMembersPagePattern.test(url)) {
			return 'groupMembersPage'
		} else {
			return 'unknownPage'
		}
	}

	const openSendBulkMessage = (): void => {
		emittedEvent.value = 'send-bulk-message'
	}

	const openSendGroupMessage = (): void => {
		emittedEvent.value = 'send-group-message'
	}

	const openSendMemberMessage = (): void => {
		administratorStore.selectedMembers = []
		emittedEvent.value = 'send-member-message'
	}

	const resetCAModal = (): void => {
		emittedEvent.value = ''
	}

	const scrollToTop = (scrollContainerId?: string): void => {
		const scrollContainer = scrollContainerId
			? document.getElementById(scrollContainerId)
			: undefined

		if (scrollContainer) {
			if (scrollContainer.scrollHeight > scrollContainer.clientHeight) {
				scrollContainer.scrollTo({ behavior: 'smooth', top: 0 })
			}
		} else {
			window.scrollTo({ behavior: 'smooth', top: 0 })
		}
	}

	return {
		// State
		agencies,
		agency,
		agencyBackOffice,
		agencyCreatedAt,
		agencyInfo,
		agencyMembers,
		bulkAction,
		currentUser,
		emittedEvent,
		franchisorSSOPath,
		isAdvanceReporting,
		isSharedCompletionsEnabled,
		loadingAgencyInfo,
		page,
		previousRoute,
		previousRouteUrl,
		selectAllChecked,
		selectAllCheckedOnPage,
		selectedCATableMembers,
		selectedLanguage,
		showPopover,
		showTrainingDashboard,
		simpleHeaderLayout,
		twilioSuccessCode,
		twilioUserErrorMessages,
		userId,

		// Getters
		agencyId,
		checkedOnAnotherPage,
		currentDate,
		currentDateString,
		currentUrl,
		eligibleAgenciesUrl,
		franchisorId,
		franchisorLink,
		hasAgency,
		hasFranchisorId,
		inFreeTrial,
		isAdvanceReportingEnabled,
		isBillingAdmin,
		isCurriculumDiscoverabilityEnabled,
		isFranchisor,
		isFromTeamPage,
		isMemberOnly,
		isMobileView,
		isMultiLocation,
		pageIsDashboard,
		pageIsFranchisor,
		selectedCheckboxNotOnPage,
		showAddAllToOrganizations,
		showFranchise,
		someSelectAllCheckboxIsTrue,
		states,
		teamBreadcrumbLink,

		// Actions
		checkAndDisplaySessionStorageMessage,
		clearSelectedValues,
		closeModal,
		closePopover,
		countDownTimer,
		fillAgencyBackOffice,
		formatSeatsUsed,
		getAgencyInfo,
		getTwilioUserErrorMessage,
		hasTwilioUserError,
		identifyPage,
		openSendBulkMessage,
		openSendGroupMessage,
		openSendMemberMessage,
		resetCAModal,
		scrollToTop,
	}
})
